import { useEffect, useState, type ReactNode } from "react";

import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";
import {
  formatReviewProgressLossPhrase,
  type ReviewProgressSummary,
} from "@/components/case-checking/guidedReviewProgress";
import {
  getGuidedReviewRestorePreview,
  type CaseCheckRerunFields,
  type GuidedReviewRestorePreview,
  type ReviewOnRerunMode,
} from "@/services/api/case-checking";

export type CaseRerunConfirmAction = "submit_now" | "tonight" | "recover" | "retry_classification";

export type CaseRerunConfirmOptions = CaseCheckRerunFields;

const DESCRIPTION_CLASS = "text-sm text-slate-600 dark:text-slate-300";

function rerunFromTriageText(): string {
  return "Ani will re-run the case from document triage in the background. New or updated documents may change completeness, assessment, and grades.";
}

function rerunOvernightText(): string {
  return "Queued documents will be added overnight, then Ani re-runs from document triage. Results may change.";
}

function retryClassificationIntroText(): string {
  return "Ani will retry classification for documents that failed or are not yet classified, then continue from document triage.";
}

function progressSentence(progress: ReviewProgressSummary, lossPhrase: string): string {
  const savedSuffix = lossPhrase ? `, with ${lossPhrase} saved` : "";
  return `You're on ${progress.currentStepLabel}${savedSuffix}.`;
}

function descriptionParagraphs(paragraphs: string[]): ReactNode {
  if (paragraphs.length === 0) return null;
  return (
    <div className="space-y-3">
      {paragraphs.map((body, index) => (
        <p key={index} className={DESCRIPTION_CLASS}>{body}</p>
      ))}
    </div>
  );
}

export function buildCaseRerunConfirmOptions(
  hasProgress: boolean,
  mode: ReviewOnRerunMode,
  sourceReviewRunId: string | null | undefined,
): CaseRerunConfirmOptions {
  if (!hasProgress) return {};
  return {
    acknowledge_rerun: true,
    review_on_rerun: mode,
    source_review_run_id: mode === "carry" ? sourceReviewRunId ?? undefined : undefined,
  };
}

function formatRestorePreviewSummary(preview: GuidedReviewRestorePreview): string {
  const parts: string[] = [];
  const r = preview.will_recover;
  if (r.reviews > 0) parts.push(`${r.reviews} rule change${r.reviews === 1 ? "" : "s"}`);
  if (r.checklist_disputes > 0) parts.push(`${r.checklist_disputes} checklist note${r.checklist_disputes === 1 ? "" : "s"}`);
  if (r.remedial_edits > 0) parts.push(`${r.remedial_edits} remedial edit${r.remedial_edits === 1 ? "" : "s"}`);
  if (r.completeness_review) parts.push("completeness decision");
  if (r.completed_steps > 0) parts.push(`${r.completed_steps} completed step${r.completed_steps === 1 ? "" : "s"}`);
  if (parts.length === 0) return "Nothing to restore for the current rules.";
  let summary = `Can restore ${parts.join(", ")}.`;
  if (preview.dropped_rule_ids.length > 0) {
    summary += ` ${preview.dropped_rule_ids.length} rule override${preview.dropped_rule_ids.length === 1 ? "" : "s"} no longer apply and will be skipped.`;
  }
  return summary;
}

export function caseRerunConfirmCopy(
  action: CaseRerunConfirmAction,
  progress: ReviewProgressSummary | null | undefined,
  options?: { reviewMode?: ReviewOnRerunMode },
): { title: string; description: ReactNode; confirmLabel: string } {
  const hasProgress = Boolean(progress?.hasProgress);
  const lossPhrase = progress ? formatReviewProgressLossPhrase(progress) : "";
  const carry = options?.reviewMode === "carry";

  const withProgressParagraphs = (lead: string): string[] => {
    if (!progress) return [lead];
    const paragraphs = [lead, progressSentence(progress, lossPhrase)];
    return paragraphs;
  };

  if (action === "tonight") {
    const title = "Process queued documents tonight?";
    if (hasProgress && progress) {
      return {
        title,
        description: descriptionParagraphs(withProgressParagraphs(rerunOvernightText())),
        confirmLabel: "Schedule tonight",
      };
    }
    return {
      title,
      description: descriptionParagraphs([rerunOvernightText()]),
      confirmLabel: "Schedule tonight",
    };
  }

  if (action === "retry_classification") {
    const title = "Retry document classification?";
    if (hasProgress && progress) {
      const lead = `${retryClassificationIntroText()} This runs in the background and can take a while. Updated classifications may change later assessment results.`;
      return {
        title,
        description: descriptionParagraphs(withProgressParagraphs(lead)),
        confirmLabel: carry ? "Retry and carry review" : "Retry classification",
      };
    }
    return {
      title,
      description: descriptionParagraphs([
        `${retryClassificationIntroText()} This runs in the background. Updated classifications may change later assessment results.`,
      ]),
      confirmLabel: "Retry classification",
    };
  }

  if (action === "recover") {
    const title = "Re-run case check?";
    if (hasProgress && progress) {
      return {
        title,
        description: descriptionParagraphs(withProgressParagraphs(rerunFromTriageText())),
        confirmLabel: carry ? "Re-run and carry review" : "Re-run case check",
      };
    }
    return {
      title,
      description: descriptionParagraphs([rerunFromTriageText()]),
      confirmLabel: "Re-run case check",
    };
  }

  const title = "Submit queued documents?";
  if (hasProgress && progress) {
    return {
      title,
      description: descriptionParagraphs(withProgressParagraphs(rerunFromTriageText())),
      confirmLabel: carry ? "Submit and carry review" : "Submit and start fresh",
    };
  }

  return {
    title,
    description: descriptionParagraphs([rerunFromTriageText()]),
    confirmLabel: "Submit now",
  };
}

function ReviewOnRerunChoice({
  mode,
  onModeChange,
  preview,
  previewLoading,
  previewError,
}: {
  mode: ReviewOnRerunMode;
  onModeChange: (mode: ReviewOnRerunMode) => void;
  preview: GuidedReviewRestorePreview | null;
  previewLoading: boolean;
  previewError: string | null;
}) {
  return (
    <div className="mt-4 space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-slate-900/40">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">After re-check</p>
      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
        <input
          type="radio"
          name="review_on_rerun"
          className="mt-1"
          checked={mode === "carry"}
          onChange={() => onModeChange("carry")}
          data-testid="review-on-rerun-carry"
        />
        <span className="font-medium">Carry my review</span>
      </label>
      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
        <input
          type="radio"
          name="review_on_rerun"
          className="mt-1"
          checked={mode === "fresh"}
          onChange={() => onModeChange("fresh")}
          data-testid="review-on-rerun-fresh"
        />
        <span>
          <span className="font-medium">Start fresh</span>
          <span className="block text-slate-500 dark:text-slate-400">Clear this run&apos;s guided review. Earlier runs stay in Previous reviews.</span>
        </span>
      </label>
      {mode === "carry" ? (
        <div className="text-xs text-slate-600 dark:text-slate-300" data-testid="review-on-rerun-carry-preview">
          {previewLoading ? <p>Loading restore preview…</p> : null}
          {previewError ? <p className="text-rose-600 dark:text-rose-300">{previewError}</p> : null}
          {preview && !previewLoading ? <p>{formatRestorePreviewSummary(preview)}</p> : null}
        </div>
      ) : null}
      <p className="text-xs text-slate-600 dark:text-slate-300">
        Your current review is kept in Previous reviews either way.
      </p>
    </div>
  );
}

export function ConfirmCaseRerunDialog({
  open,
  onClose,
  onConfirm,
  action,
  progress,
  caseId,
  sourceReviewRunId,
  isLoading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: CaseRerunConfirmOptions) => void;
  action: CaseRerunConfirmAction;
  progress?: ReviewProgressSummary | null;
  caseId?: string;
  sourceReviewRunId?: string | null;
  isLoading?: boolean;
  /** @deprecated No longer changes copy; kept for call-site compatibility. */
  embedNoSession?: boolean;
}) {
  const hasProgress = Boolean(progress?.hasProgress);
  const [reviewMode, setReviewMode] = useState<ReviewOnRerunMode>("carry");
  const [preview, setPreview] = useState<GuidedReviewRestorePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReviewMode("carry");
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !hasProgress || reviewMode !== "carry" || !caseId || !sourceReviewRunId) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void getGuidedReviewRestorePreview(caseId, sourceReviewRunId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(err instanceof Error ? err.message : "Could not load restore preview");
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, hasProgress, open, reviewMode, sourceReviewRunId]);

  const copy = caseRerunConfirmCopy(action, progress, { reviewMode: hasProgress ? reviewMode : undefined });

  const description = hasProgress ? (
    <>
      {copy.description}
      <ReviewOnRerunChoice
        mode={reviewMode}
        onModeChange={setReviewMode}
        preview={preview}
        previewLoading={previewLoading}
        previewError={previewError}
      />
    </>
  ) : (
    copy.description
  );

  const handleConfirm = () => {
    onConfirm(buildCaseRerunConfirmOptions(hasProgress, reviewMode, sourceReviewRunId));
  };

  return (
    <ConfirmDeleteDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={copy.title}
      description={description}
      confirmLabel={copy.confirmLabel}
      cancelLabel="Cancel"
      isDestructive={false}
      confirmButtonColor="indigo"
      isLoading={isLoading}
      size={hasProgress ? "xl" : "md"}
    />
  );
}
